import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { SearchLg } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { useChatAI } from "@/components/application/ai-chat/chat-ai-context";
import type { AskEveClientContext } from "@/types/ask-eve-client-context";
import { ASK_EVE_FORENSICS_PRESETS, ASK_EVE_RISK_PROMPT } from "@/components/application/ai-chat/ask-eve-debug-presets";
import { apiGet } from "@/lib/api-client";
import type { ContainerTableResponse, ContainerTableRow } from "@/types/api/containers-recap";
import { cx } from "@/utils/cx";

function hasResolvableContainer(ctx: AskEveClientContext | null): boolean {
    if (!ctx?.entity || ctx.entity.kind !== "container") return false;
    const id = ctx.entity.id;
    const num = ctx.entity.container_number;
    if (id && String(id).trim().length > 0) return true;
    if (num != null && String(num).trim().length > 0) return true;
    return false;
}

function formatContainerPickLabel(c: ContainerTableRow): string {
    const refParts = [c.bl_number, c.booking_number].filter((x) => x != null && String(x).trim() !== "");
    const ref = refParts.length ? refParts.join(" · ") : "";
    const num = c.container_number?.trim();
    if (ref && num) return `${ref} — ${num}`;
    return num || ref || String(c.container_id).slice(0, 8);
}

/**
 * Pulsante "Debug container" + menu con le azioni (solo shipper, accanto alle altre quick action sopra l'input).
 */
export function AskEveDebugContainerDropdown({ className }: { className?: string }) {
    const { t } = useTranslation("common");
    const location = useLocation();
    const { submitPrompt, openAskEvePanel, askEveClientContext, setAskEveClientContext } = useChatAI();
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedPick, setSelectedPick] = useState<ContainerTableRow | null>(null);
    const [suggestFocused, setSuggestFocused] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const [panelStyle, setPanelStyle] = useState<CSSProperties>({});

    useEffect(() => {
        const q = searchQuery.trim();
        if (!q) {
            setDebouncedSearch("");
            return;
        }
        const t = window.setTimeout(() => setDebouncedSearch(q), 300);
        return () => window.clearTimeout(t);
    }, [searchQuery]);

    /**
     * Popover in portal su `document.body`: dentro pannelli con `transform`, `position:fixed`
     * è relativo al pannello e i pixel da getBoundingClientRect() (viewport) non combaciano → popover in alto a sinistra.
     * Sul body, `fixed` + coordinate viewport sono corrette.
     */
    useLayoutEffect(() => {
        if (!open || !anchorRef.current) return;
        const place = () => {
            const el = anchorRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            const maxW = Math.min(22 * 16, window.innerWidth - 24);
            const left = Math.max(8, Math.min(r.left, window.innerWidth - maxW - 8));
            const gap = 8;
            setPanelStyle({
                position: "fixed",
                left,
                top: r.top - gap,
                transform: "translateY(-100%)",
                width: maxW,
                zIndex: 10060,
            });
        };
        const id = requestAnimationFrame(place);
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => {
            cancelAnimationFrame(id);
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) setPanelStyle({});
    }, [open]);

    useEffect(() => {
        if (!open) {
            setSearchQuery("");
            setDebouncedSearch("");
            setSelectedPick(null);
            setSuggestFocused(false);
        }
    }, [open]);

    const pageHasContainer = hasResolvableContainer(askEveClientContext);

    const { data: suggestData, isFetching: suggestLoading } = useQuery({
        queryKey: ["ask-eve-debug-container-suggest", debouncedSearch],
        queryFn: async () => {
            return apiGet<ContainerTableResponse>("/containers-recap/table", {
                params: {
                    limit: 12,
                    skip: 0,
                    ...(debouncedSearch ? { search: debouncedSearch } : {}),
                },
            });
        },
        enabled: open && !pageHasContainer,
        staleTime: 20_000,
    });
    const suggestions = suggestData?.containers ?? [];

    const canRunAction = pageHasContainer || selectedPick != null;

    const buildMergedContext = useCallback(
        (pick: ContainerTableRow): AskEveClientContext => {
            const base: AskEveClientContext = askEveClientContext
                ? { ...askEveClientContext }
                : { surface: "ask_eve_panel", route: location.pathname };
            return {
                ...base,
                route: base.route ?? location.pathname,
                entity: {
                    kind: "container",
                    id: pick.container_id,
                    container_number: pick.container_number ?? null,
                    bl_number: pick.bl_number ?? null,
                    booking_number: pick.booking_number ?? null,
                },
            };
        },
        [askEveClientContext, location.pathname],
    );

    const buildForensicsPrompt = (question: string, containerHint: string) =>
        `${question}${containerHint}\n\n` +
        `Usa analyze_container_history per un'analisi approfondita e mostrami il report.`;

    const runPrompt = useCallback(
        (prompt: string) => {
            if (!pageHasContainer && selectedPick) {
                setAskEveClientContext(buildMergedContext(selectedPick));
            }
            openAskEvePanel();
            submitPrompt(prompt);
            setOpen(false);
            setSearchQuery("");
            setSelectedPick(null);
        },
        [
            pageHasContainer,
            selectedPick,
            buildMergedContext,
            setAskEveClientContext,
            openAskEvePanel,
            submitPrompt,
        ],
    );

    const containerHintLabel =
        askEveClientContext?.entity?.container_number != null
            ? String(askEveClientContext.entity.container_number)
            : selectedPick?.container_number != null
              ? selectedPick.container_number
              : "";

    const runRisk = useCallback(() => {
        if (!canRunAction) return;
        const hint = containerHintLabel ? ` Contesto: container ${containerHintLabel}.` : "";
        runPrompt(`${ASK_EVE_RISK_PROMPT}${hint}`);
    }, [canRunAction, containerHintLabel, runPrompt]);

    const runForensics = useCallback(
        (presetId: string) => {
            if (!canRunAction) return;
            const preset = ASK_EVE_FORENSICS_PRESETS.find((p) => p.id === presetId);
            if (!preset) return;
            const containerHint = containerHintLabel ? ` (container ${containerHintLabel})` : "";
            runPrompt(buildForensicsPrompt(preset.question, containerHint));
        },
        [canRunAction, containerHintLabel, runPrompt],
    );

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open]);

    const overlay =
        open &&
        typeof document !== "undefined" &&
        createPortal(
            <>
                <div
                    className="fixed inset-0 z-[10050] bg-black/20"
                    aria-hidden
                    onClick={() => setOpen(false)}
                />
                <div
                    role="dialog"
                    style={panelStyle}
                    aria-label={t("askEve.containerDebug.title", { defaultValue: "Debug container" })}
                    className="max-h-[min(70vh,28rem)] overflow-y-auto rounded-xl border border-secondary bg-primary p-3 shadow-lg ring-1 ring-black/5 dark:ring-white/10 z-[10060]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <p className="mb-2 text-xs font-medium text-tertiary">
                        {t("askEve.containerDebug.title", { defaultValue: "Debug container" })}
                    </p>

                    {!pageHasContainer ? (
                        <div className="mb-3 space-y-1.5 rounded-lg border border-secondary bg-secondary_subtle p-2">
                            <p className="text-xs text-tertiary">
                                {t("askEve.containerDebug.needIds", {
                                    defaultValue:
                                        "Se non sei sul dettaglio container, cerca per B/L o numero prenotazione e scegli un risultato dall’elenco.",
                                })}
                            </p>
                            <label className="block text-xs font-medium text-secondary">
                                {t("askEve.containerDebug.blOrBooking", {
                                    defaultValue: "B/L o numero prenotazione",
                                })}
                            </label>
                            <div className="relative">
                                <input
                                    type="search"
                                    autoComplete="off"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        if (selectedPick) setSelectedPick(null);
                                    }}
                                    onFocus={() => setSuggestFocused(true)}
                                    onBlur={() => {
                                        window.setTimeout(() => setSuggestFocused(false), 200);
                                    }}
                                    placeholder={t("askEve.containerDebug.searchPlaceholder", {
                                        defaultValue: "Cerca B/L, booking, container…",
                                    })}
                                    className="w-full rounded-lg border border-secondary bg-primary px-2.5 py-2 text-sm text-primary placeholder:text-quaternary focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                                />
                                {selectedPick ? (
                                    <p className="mt-1 text-xs text-success-600 dark:text-success-400">
                                        {t("askEve.containerDebug.selected", { defaultValue: "Container selezionato" })}
                                    </p>
                                ) : null}
                                {suggestFocused && (suggestLoading || suggestions.length > 0) ? (
                                    <ul
                                        className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-secondary bg-primary py-1 shadow-lg ring-1 ring-black/5 dark:ring-white/10"
                                        role="listbox"
                                    >
                                        {suggestLoading ? (
                                            <li className="px-2.5 py-2 text-xs text-quaternary">
                                                {t("askEve.containerDebug.loading", { defaultValue: "Caricamento…" })}
                                            </li>
                                        ) : (
                                            suggestions.map((c) => (
                                                <li key={c.container_id} role="option">
                                                    <button
                                                        type="button"
                                                        className="flex w-full flex-col items-start gap-0.5 px-2.5 py-2 text-left text-xs hover:bg-secondary"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => {
                                                            setSelectedPick(c);
                                                            setSearchQuery(formatContainerPickLabel(c));
                                                            setSuggestFocused(false);
                                                        }}
                                                    >
                                                        <span className="font-medium text-primary">
                                                            {formatContainerPickLabel(c)}
                                                        </span>
                                                        <span className="text-[11px] text-quaternary">
                                                            {[c.origin_port_name, c.destination_port_name]
                                                                .filter(Boolean)
                                                                .join(" → ")}
                                                            {c.carrier_name ? ` · ${c.carrier_name}` : ""}
                                                        </span>
                                                    </button>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                ) : null}
                                {suggestFocused && !suggestLoading && debouncedSearch.length > 0 && suggestions.length === 0 ? (
                                    <p className="absolute left-0 right-0 top-full z-10 mt-1 rounded-lg border border-secondary bg-primary px-2.5 py-2 text-xs text-quaternary shadow-lg">
                                        {t("askEve.containerDebug.noResults", { defaultValue: "Nessun container trovato" })}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    <div className="flex flex-col gap-1.5">
                        <Button
                            type="button"
                            size="sm"
                            color="secondary"
                            className="w-full justify-start"
                            onClick={runRisk}
                            isDisabled={!canRunAction}
                        >
                            {t("askEve.containerDebug.risk", { defaultValue: "Valuta rischio" })}
                        </Button>
                        {ASK_EVE_FORENSICS_PRESETS.map((p) => (
                            <Button
                                key={p.id}
                                type="button"
                                size="sm"
                                color="secondary"
                                className="w-full justify-start"
                                onClick={() => runForensics(p.id)}
                                isDisabled={!canRunAction}
                            >
                                {p.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </>,
            document.body,
        );

    return (
        <div className={cx("relative flex-shrink-0", className)}>
            <div ref={anchorRef} className="inline-flex">
                <Button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    color="secondary"
                    size="sm"
                    className="text-sm"
                    iconLeading={SearchLg}
                >
                    {t("askEve.containerDebug.button", { defaultValue: "Debug container" })}
                </Button>
            </div>
            {overlay}
        </div>
    );
}
